import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { StoreRegion } from '@/lib/graphql/generated/graphql';
import { getLocaleFromLanguage } from '@/lib/hooks/useCountryNames';
import { useGetAllCountryLabels } from '@/lib/hooks/useGetAllCountryLabels';
import { useGetTopCountryLabels } from '@/lib/hooks/useGetTopCountryLabels';
import { cn } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import { Store } from '@/types/store';
import { useAtomValue } from 'jotai';
import { Check } from 'lucide-react';

interface CountryListProps {
  selectedCode?: string | null;
  onSelect: (code: string) => void;
  emptyMessage?: string;
  countryNames: Record<string, string>;
}

/**
 * Convert Store enum to StoreRegion enum
 */
const storeToStoreRegion = (store: Store): StoreRegion => {
  return store === Store.FR ? StoreRegion.Fr : StoreRegion.Us;
};

export function CountryList({
  selectedCode,
  onSelect,
  emptyMessage,
  countryNames,
}: CountryListProps) {
  const { t, i18n } = useTranslation();
  const store = useAtomValue(storeAtom);
  const storeRegion = storeToStoreRegion(store);

  const { countryLabels: allCountryLabels } = useGetAllCountryLabels();
  const { countryLabels: topCountryLabels } =
    useGetTopCountryLabels(storeRegion);

  const topCountryCodes = useMemo(
    () => new Set(topCountryLabels.map((label) => label.code)),
    [topCountryLabels]
  );

  const { topCountries, otherCountries } = useMemo(() => {
    const top: Array<{ code: string; name: string }> = [];
    const other: Array<{ code: string; name: string }> = [];

    allCountryLabels.forEach((label) => {
      const name = countryNames[label.code] ?? label.code;
      const country = { code: label.code, name };

      if (topCountryCodes.has(label.code)) {
        top.push(country);
      } else {
        other.push(country);
      }
    });

    const locale = getLocaleFromLanguage(i18n.language);
    top.sort((a, b) => a.name.localeCompare(b.name, locale));
    other.sort((a, b) => a.name.localeCompare(b.name, locale));

    return { topCountries: top, otherCountries: other };
  }, [allCountryLabels, topCountryCodes, countryNames, i18n.language]);

  const renderCountryItem = (country: { code: string; name: string }) => (
    <CommandItem
      key={country.code}
      value={country.name}
      className="cursor-pointer"
      onSelect={() => onSelect(country.code)}
    >
      <Check
        className={cn(
          'mr-2 h-4 w-4',
          selectedCode === country.code ? 'opacity-100' : 'opacity-0'
        )}
      />
      {country.name}
    </CommandItem>
  );

  return (
    <CommandList>
      <CommandEmpty>
        {emptyMessage ?? t('kpi.made_in_research_no_country_found')}
      </CommandEmpty>

      {topCountries.length > 0 && (
        <CommandGroup heading={t('kpi.top_countries')}>
          {topCountries.map(renderCountryItem)}
        </CommandGroup>
      )}

      {otherCountries.length > 0 && (
        <CommandGroup heading={t('kpi.all_countries')}>
          {otherCountries.map(renderCountryItem)}
        </CommandGroup>
      )}
    </CommandList>
  );
}
